import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { SparkIcon } from '../SparkIcon';
import './ChoiceTile.css';

export type ChoiceTileSelectionMode = 'single' | 'multiple';

/** Compact option tile (Figma waiting-list / onboarding wizard answer rows). */
export type ChoiceTileProps = {
  /** Option label (base/regular, left-aligned). */
  label: ReactNode;
  /** Marks the tile as a current choice (active border, tint and trailing check). */
  selected?: boolean;
  /**
   * `'single'` (default) exposes the tile as `role="radio"` for one-answer
   * questions; `'multiple'` exposes it as `role="checkbox"` for pick-many
   * questions (Figma onboarding: "What do you want to improve", "Who is the
   * model for"). Visuals are identical in both modes.
   */
  selectionMode?: ChoiceTileSelectionMode;
  disabled?: boolean;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'disabled' | 'type' | 'role'>;

/**
 * ChoiceTile — a 40px-tall text-only pick tile used in option lists and
 * two-column option grids. The trailing 28px check slot is always laid out
 * so the label never shifts when the tile is (de)selected; the 24px `check`
 * icon inside it is rendered only while `selected` (Figma 9242:4258).
 *
 * Chrome follows Figma node 9210:3647 / 9210:3703 (waiting-list wizard) and
 * 9237:3371 (onboarding): white surface, 1px `--semantic-border-default`,
 * 8px radius, 16px/8px padding; selected = `--primitive-blue-200` tint +
 * `--semantic-border-active`.
 *
 * In `selectionMode="single"` tiles are `role="radio"` so a wrapping
 * `role="radiogroup"` reads as a single-select question; in
 * `selectionMode="multiple"` they are `role="checkbox"` so a wrapping
 * `role="group"` reads as a multi-select question.
 */
export const ChoiceTile = forwardRef<HTMLButtonElement, ChoiceTileProps>(function ChoiceTile(
  { label, selected = false, selectionMode = 'single', disabled = false, className = '', ...rest },
  ref,
) {
  const classes = ['spark-choice-tile', selected ? 'spark-choice-tile-selected' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type="button"
      role={selectionMode === 'multiple' ? 'checkbox' : 'radio'}
      aria-checked={selected}
      className={classes}
      disabled={disabled}
      {...rest}
    >
      <span className="spark-choice-tile-label">{label}</span>
      <span className="spark-choice-tile-check-slot" aria-hidden="true">
        {selected ? <SparkIcon slug="check" size="medium" className="spark-choice-tile-check" /> : null}
      </span>
    </button>
  );
});
